import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import { fileUrl } from "../../lib/fileUrl";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { udmInputLabel } from "../../utils/udmUtils";
import {
  ArrowLeft, ArrowRight, Plus, Trash2, X, Mic, MicOff, ImagePlus,
  Check, AlertTriangle, Save, ChevronLeft, ListChecks, Package
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerFooter } from "../../components/ui/drawer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const normalize = (str) =>
  (str || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

// Drawer per selezionare/modificare ingredienti nello step
const IngredientiStepDrawer = ({ open, onOpenChange, ingredienti, stepData, currentStep, steps, onToggle, onUpdateQty, onSetTutto, getRemainingQty }) => {
  const getIngNome = (u) => typeof u === 'object' ? u.nome : u;
  const getIngQty = (u) => typeof u === 'object' ? (u.quantita || 0) : 0;
  const findIngInStep = (nome) => (stepData?.ingredienti_usati || []).find(u => getIngNome(u) === nome);

  const handleDone = () => {
    const invalidItems = (stepData?.ingredienti_usati || []).filter(u => getIngQty(u) === 0);
    if (invalidItems.length > 0) {
      const names = invalidItems.map(u => getIngNome(u)).join(", ");
      toast.error(`Inserisci la quantità per: ${names}`);
      return;
    }
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[80vh]" data-testid="ingredienti-step-drawer">
        <DrawerHeader className="text-left">
          <DrawerTitle>Ingredienti - Step {currentStep + 1}</DrawerTitle>
        </DrawerHeader>
        <div className="px-4 pb-4 overflow-y-auto flex-1 space-y-2">
          {ingredienti.map((ing, i) => {
            const found = findIngInStep(ing.nome);
            const isUsed = !!found;
            const currentQty = found ? getIngQty(found) : 0;
            const totalRecipe = ing.quantita || 0;
            const unit = ing.is_acqua ? "ml" : udmInputLabel(ing?.unita_misura || "g");
            const remaining = getRemainingQty(ing.nome);
            const isExhausted = !isUsed && totalRecipe > 0 && remaining <= 0;
            const hasQtyError = isUsed && currentQty === 0;
            // Count usage in other steps
            let usedOther = 0;
            steps.forEach((s, si) => {
              if (si !== currentStep) {
                const f = (s.ingredienti_usati || []).find(u => getIngNome(u) === ing.nome);
                if (f) usedOther += getIngQty(f);
              }
            });

            return (
              <div key={i} className={`rounded-xl border transition-colors ${
                isUsed
                  ? hasQtyError ? 'border-red-500/40 bg-red-500/5' : 'border-green-500/40 bg-green-500/5'
                  : isExhausted ? 'border-border/30 bg-muted/20 opacity-50' : 'border-border'
              }`}>
                <button type="button" onClick={() => !isExhausted && onToggle(ing.nome)}
                  disabled={isExhausted}
                  className={`w-full flex items-center justify-between px-4 py-3 text-left ${isExhausted ? 'cursor-not-allowed' : ''}`}
                  data-testid={`drawer-ing-${i}`}>
                  <div className="flex items-center gap-2.5">
                    {isUsed ? (
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center ${hasQtyError ? 'bg-red-500' : 'bg-green-600'}`}>
                        <Check className="h-3.5 w-3.5 text-white" />
                      </div>
                    ) : (
                      <div className={`w-6 h-6 rounded-full border-2 ${isExhausted ? 'border-muted-foreground/20' : 'border-muted-foreground/30'}`} />
                    )}
                    <span className={`font-medium text-sm ${isExhausted ? 'text-muted-foreground/50' : ''}`}>{ing.nome}</span>
                    {isExhausted && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground/60 font-medium">Esaurito</span>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-muted-foreground">{totalRecipe}{unit}</span>
                    {usedOther > 0 && <p className="text-[10px] text-muted-foreground">usato: {usedOther}{unit}</p>}
                  </div>
                </button>
                {isUsed && (
                  <div className="px-4 pb-3 flex items-center gap-2 flex-wrap">
                    <Input type="number" inputMode="numeric" value={currentQty || ""} onChange={(e) => onUpdateQty(ing.nome, e.target.value)}
                      placeholder="qtà" className={`h-10 w-24 text-center ${hasQtyError ? 'border-red-500 focus-visible:ring-red-500' : ''}`}
                      data-testid={`drawer-qty-${i}`} />
                    <span className="text-xs text-muted-foreground">{unit}</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => onSetTutto(ing.nome)}
                      className="h-10 px-3 text-xs font-bold text-green-600 border-green-500/30"
                      data-testid={`drawer-tutto-${i}`}>
                      Tutto ({remaining}{unit})
                    </Button>
                    {hasQtyError && (
                      <span className="text-xs text-red-500 font-medium w-full">Inserisci una quantità o premi &quot;Tutto&quot;</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <DrawerFooter className="border-t">
          <Button className="w-full h-12 font-bold" onClick={handleDone} data-testid="drawer-ing-done">Fatto</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

const ProceduraRicetta = () => {
  const { ricettaId } = useParams();
  const [searchParams] = useSearchParams();
  const readOnly = searchParams.get("view") === "1";
  const navigate = useNavigate();
  const { getAuthHeaders, canManageRicette } = useAuth();

  const [ricettaNome, setRicettaNome] = useState("");
  const [ingredienti, setIngredienti] = useState([]);
  const [steps, setSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showOverview, setShowOverview] = useState(true);
  const [ingDrawerOpen, setIngDrawerOpen] = useState(false);

  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  const fotoInputRef = useRef(null);
  const [uploadingFoto, setUploadingFoto] = useState(false);

  const canEdit = canManageRicette && !readOnly;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/ricette/${ricettaId}/procedura`, { headers: getAuthHeaders() });
        setRicettaNome(res.data.nome);
        setIngredienti(res.data.ingredienti || []);
        const proc = res.data.procedimento || [];
        if (proc.length > 0) {
          setSteps(proc.map(s => ({
            descrizione: s.descrizione || "",
            foto_urls: s.foto_urls || [],
            ingredienti_usati: (s.ingredienti_usati || []).map(u =>
              typeof u === 'string' ? { nome: u, quantita: 0 } : u
            )
          })));
        } else if (canEdit) {
          setSteps([{ descrizione: "", foto_urls: [], ingredienti_usati: [] }]);
        }
      } catch {
        toast.error("Errore nel caricamento della ricetta");
        navigate(-1);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [ricettaId]);

  // Step management
  const addStep = () => {
    setSteps(prev => [...prev, { descrizione: "", foto_urls: [], ingredienti_usati: [] }]);
    setCurrentStep(steps.length);
  };

  const removeCurrentStep = () => {
    if (steps.length <= 1) return;
    setSteps(prev => prev.filter((_, i) => i !== currentStep));
    setCurrentStep(prev => Math.max(0, prev - 1));
  };

  const updateDescrizione = (value) => {
    setSteps(prev => {
      const updated = [...prev];
      updated[currentStep] = { ...updated[currentStep], descrizione: value };
      return updated;
    });
  };

  // Ingredient helpers
  const getIngNome = (u) => typeof u === 'object' ? u.nome : u;
  const getIngQty = (u) => typeof u === 'object' ? (u.quantita || 0) : 0;
  const findIngInStep = (nome, stepData) => (stepData?.ingredienti_usati || []).find(u => getIngNome(u) === nome);

  const toggleIngrediente = (nome) => {
    setSteps(prev => {
      const updated = [...prev];
      const step = { ...updated[currentStep] };
      const usati = [...(step.ingredienti_usati || [])];
      const idx = usati.findIndex(u => getIngNome(u) === nome);
      if (idx >= 0) usati.splice(idx, 1);
      else usati.push({ nome, quantita: 0 });
      step.ingredienti_usati = usati;
      updated[currentStep] = step;
      return updated;
    });
  };

  const updateIngredienteQuantita = (nome, quantita) => {
    setSteps(prev => {
      const updated = [...prev];
      const step = { ...updated[currentStep] };
      const usati = [...(step.ingredienti_usati || [])];
      const idx = usati.findIndex(u => getIngNome(u) === nome);
      if (idx >= 0) {
        usati[idx] = { nome, quantita: parseFloat(quantita) || 0 };
        step.ingredienti_usati = usati;
        updated[currentStep] = step;
      }
      return updated;
    });
  };

  const getRemainingQty = useCallback((nome) => {
    const ing = ingredienti.find(i => i.nome === nome);
    const totalRecipe = ing?.quantita || 0;
    let usedElsewhere = 0;
    steps.forEach((s, i) => {
      if (i !== currentStep) {
        const found = findIngInStep(nome, s);
        if (found) usedElsewhere += getIngQty(found);
      }
    });
    return Math.max(0, totalRecipe - usedElsewhere);
  }, [steps, currentStep, ingredienti]);

  const setTuttoIngrediente = (nome) => {
    const remaining = getRemainingQty(nome);
    updateIngredienteQuantita(nome, remaining);
  };

  // Auto-detect ingredienti nel testo
  const detectIngredientsInText = useCallback((text) => {
    if (!text) return;
    const normalizedText = normalize(text);
    const usati = [...(steps[currentStep]?.ingredienti_usati || [])];
    let changed = false;

    ingredienti.forEach(ing => {
      const normalizedName = normalize(ing.nome);
      if (normalizedName.length < 3 || !normalizedText.includes(normalizedName)) return;
      const alreadyAdded = usati.some(u => getIngNome(u) === ing.nome);
      let detectedQty = 0;
      const qtyPatterns = [
        new RegExp(`(\\d+)\\s*(?:g|gr|grammi)\\s*(?:di\\s+)?${normalizedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`),
        new RegExp(`${normalizedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*(\\d+)\\s*(?:g|gr|grammi)`),
        new RegExp(`(\\d+)\\s*(?:g|gr|grammi)\\s+${normalizedName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`)
      ];
      for (const pattern of qtyPatterns) {
        const match = normalizedText.match(pattern);
        if (match) { detectedQty = parseFloat(match[1]) || 0; break; }
      }
      if (!alreadyAdded) { usati.push({ nome: ing.nome, quantita: detectedQty }); changed = true; }
      else if (detectedQty > 0) {
        const idx = usati.findIndex(u => getIngNome(u) === ing.nome);
        if (idx >= 0 && getIngQty(usati[idx]) === 0) { usati[idx] = { nome: ing.nome, quantita: detectedQty }; changed = true; }
      }
    });

    if (changed) {
      setSteps(prev => {
        const updated = [...prev];
        updated[currentStep] = { ...updated[currentStep], ingredienti_usati: usati };
        return updated;
      });
    }
  }, [steps, currentStep, ingredienti]);

  // Voice
  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { toast.error("Browser non supporta il riconoscimento vocale"); return; }
    const recognition = new SpeechRecognition();
    recognition.lang = "it-IT";
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.onresult = (event) => {
      let transcript = "";
      for (let i = 0; i < event.results.length; i++) transcript += event.results[i][0].transcript;
      const currentText = steps[currentStep]?.descrizione || "";
      const baseText = currentText.replace(/\s*\[.*?\]\s*$/, "");
      if (event.results[event.results.length - 1].isFinal) {
        const newText = (baseText ? baseText + " " : "") + transcript.trim();
        updateDescrizione(newText);
        detectIngredientsInText(newText);
      }
    };
    recognition.onerror = (event) => { if (event.error !== "no-speech") toast.error("Errore vocale: " + event.error); setIsListening(false); };
    recognition.onend = () => setIsListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setIsListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) { recognitionRef.current.stop(); recognitionRef.current = null; }
    setIsListening(false);
  };

  // Photo
  const handleFotoUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploadingFoto(true);
    try {
      const uploadedUrls = [];
      for (const file of Array.from(files)) {
        const formData = new FormData();
        formData.append("file", file);
        const res = await axios.post(`${API}/upload-image`, formData, { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } });
        uploadedUrls.push(res.data.path);
      }
      setSteps(prev => {
        const updated = [...prev];
        const step = { ...updated[currentStep] };
        step.foto_urls = [...(step.foto_urls || []), ...uploadedUrls];
        updated[currentStep] = step;
        return updated;
      });
    } catch { toast.error("Errore nel caricamento della foto"); }
    finally { setUploadingFoto(false); }
  };

  const removeFoto = (fotoIdx) => {
    setSteps(prev => {
      const updated = [...prev];
      const step = { ...updated[currentStep] };
      step.foto_urls = step.foto_urls.filter((_, i) => i !== fotoIdx);
      updated[currentStep] = step;
      return updated;
    });
  };

  // Save
  const handleSave = async () => {
    setSaving(true);
    try {
      const procedimento = steps.map((s, i) => ({
        ordine: i, descrizione: s.descrizione, foto_urls: s.foto_urls || [], ingredienti_usati: s.ingredienti_usati || []
      }));
      await axios.put(`${API}/ricette/${ricettaId}/procedura`, { procedimento }, { headers: getAuthHeaders() });
      toast.success("Procedura salvata!");
      navigate(-1);
    } catch { toast.error("Errore nel salvataggio"); }
    finally { setSaving(false); }
  };

  // Delete
  const [confirmDelete, setConfirmDelete] = useState(false);
  const handleDeleteProcedura = async () => {
    try { await axios.delete(`${API}/ricette/${ricettaId}/procedura`, { headers: getAuthHeaders() }); toast.success("Procedura cancellata"); navigate(-1); }
    catch { toast.error("Errore nella cancellazione"); }
  };

  // Missing/underdistributed
  const getMissingIngredients = useCallback(() => {
    const allUsed = new Set();
    steps.forEach(s => (s.ingredienti_usati || []).forEach(u => allUsed.add(getIngNome(u))));
    return ingredienti.filter(ing => !allUsed.has(ing.nome));
  }, [steps, ingredienti]);

  const getUnderDistributed = useCallback(() => {
    return ingredienti.filter(ing => {
      const total = ing.quantita || 0;
      if (total === 0) return false;
      let distributed = 0;
      steps.forEach(s => { const f = findIngInStep(ing.nome, s); if (f) distributed += getIngQty(f); });
      return distributed < total;
    });
  }, [steps, ingredienti]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-background"><p className="text-muted-foreground">Caricamento...</p></div>;

  // Read-only empty
  if (readOnly && steps.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 shadow-sm flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ChevronLeft className="h-5 w-5" /></Button>
          <div className="flex-1 min-w-0"><h1 className="font-bold truncate">{ricettaNome}</h1><p className="text-xs text-muted-foreground">Procedura</p></div>
        </div>
        <div className="flex flex-col items-center justify-center p-8 mt-20">
          <ListChecks className="h-12 w-12 text-muted-foreground/30 mb-4" />
          <p className="text-muted-foreground">Nessuna procedura disponibile</p>
        </div>
      </div>
    );
  }

  const step = steps[currentStep] || {};
  const missing = getMissingIngredients();
  const underDistributed = getUnderDistributed();
  const ingredientiMap = {};
  ingredienti.forEach(ing => { ingredientiMap[ing.nome] = ing; });
  const usatiInStep = step.ingredienti_usati || [];

  // SUMMARY VIEW
  if (showSummary) {
    return (
      <div className="min-h-screen bg-background" data-testid="procedura-summary">
        <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 shadow-sm flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setShowSummary(false)} data-testid="back-from-summary"><ChevronLeft className="h-5 w-5" /></Button>
          <div className="flex-1"><h1 className="font-bold">Riepilogo</h1><p className="text-xs text-muted-foreground">{ricettaNome} - {steps.length} step</p></div>
        </div>
        <div className="p-4 space-y-4 pb-24">
          {missing.length > 0 && (
            <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-xl flex items-start gap-2" data-testid="missing-ingredients-warning">
              <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div><p className="text-sm font-medium text-amber-600">Non utilizzati:</p><p className="text-sm text-amber-500 mt-1">{missing.map(m => m.nome).join(", ")}</p></div>
            </div>
          )}
          {underDistributed.length > 0 && underDistributed.filter(u => !missing.some(m => m.nome === u.nome)).length > 0 && (
            <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-xl flex items-start gap-2" data-testid="underdistributed-warning">
              <AlertTriangle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div><p className="text-sm font-medium text-blue-600">Quantita incompleta:</p>
                {underDistributed.filter(u => !missing.some(m => m.nome === u.nome)).map((u, i) => {
                  let distributed = 0;
                  steps.forEach(s => { const f = (s.ingredienti_usati || []).find(x => getIngNome(x) === u.nome); if (f && typeof f === 'object') distributed += (f.quantita || 0); });
                  return <p key={i} className="text-sm text-blue-500">{u.nome}: {distributed}/{u.quantita}{u.is_acqua ? "ml" : udmInputLabel(ingredientiMap[u.nome]?.unita_misura || "g")}</p>;
                })}
              </div>
            </div>
          )}
          {steps.map((s, idx) => (
            <div key={idx} className="p-4 bg-card rounded-xl border border-border space-y-2 active:scale-[0.98] transition-transform"
              onClick={() => { setCurrentStep(idx); setShowSummary(false); }} data-testid={`summary-step-${idx}`}>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">{idx + 1}</div>
                <span className="text-sm font-medium flex-1 truncate">{s.descrizione ? s.descrizione.substring(0, 60) + (s.descrizione.length > 60 ? "..." : "") : "Nessuna descrizione"}</span>
              </div>
              {s.foto_urls?.length > 0 && (
                <div className="flex gap-2 overflow-x-auto">{s.foto_urls.map((url, fIdx) => (<img key={fIdx} src={url.startsWith("http") ? url : fileUrl(url)} alt={`Step ${idx + 1}`} className="w-12 h-12 object-cover rounded-md border border-border flex-shrink-0" />))}</div>
              )}
              {s.ingredienti_usati?.length > 0 && (
                <div className="flex flex-wrap gap-1">{s.ingredienti_usati.map((u, i) => {
                  const nome = getIngNome(u); const qty = getIngQty(u); const ing = ingredientiMap[nome]; const unit = ing?.is_acqua ? "ml" : udmInputLabel(ing?.unita_misura || "g");
                  return <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-green-600/10 text-green-700 border border-green-600/20">{nome}{qty > 0 ? ` ${qty}${unit}` : ''}</span>;
                })}</div>
              )}
            </div>
          ))}
        </div>
        {canEdit && (
          <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 flex gap-3">
            <Button variant="outline" className="flex-1 h-12" onClick={() => setShowSummary(false)}>Modifica</Button>
            <Button className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-bold" onClick={handleSave} disabled={saving} data-testid="save-procedura-btn">
              <Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // READ-ONLY VIEW
  if (readOnly) {
    // L'immagine "hero" è quella del piatto finito → ultima foto dell'ULTIMO step con foto.
    // Fallback: prima foto disponibile in qualsiasi step.
    let heroUrl = null;
    for (let i = steps.length - 1; i >= 0 && !heroUrl; i--) {
      const fotos = steps[i]?.foto_urls || [];
      if (fotos.length > 0) heroUrl = fotos[fotos.length - 1];
    }
    const heroSrc = heroUrl ? (heroUrl.startsWith("http") ? heroUrl : fileUrl(heroUrl)) : null;

    // --- SCHERMATA 0: OVERVIEW ---
    if (showOverview) {
      return (
        <div className="min-h-screen bg-[#F5F5FA]" data-testid="procedura-readonly-overview">
          {/* Header */}
          <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/80 border-b border-border px-4 py-3 flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <span className="text-sm font-medium text-[#1A1A1E] flex-1 truncate">Ricetta</span>
          </div>

          {/* Hero Image */}
          {heroSrc ? (
            <img src={heroSrc} alt={ricettaNome} className="w-full aspect-video object-cover" />
          ) : (
            <div className="w-full aspect-video bg-gradient-to-br from-[#6C63FF]/20 to-[#6C63FF]/5 flex items-center justify-center">
              <Package className="h-16 w-16 text-[#6C63FF]/30" />
            </div>
          )}

          {/* Content */}
          <div className="p-4 space-y-5 pb-28">
            <h1 className="text-2xl font-bold tracking-tight text-[#1A1A1E]">{ricettaNome}</h1>

            {/* Metadata */}
            <div className="flex items-center gap-5">
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <ListChecks className="h-4 w-4" />
                <span>{steps.length} {steps.length === 1 ? 'step' : 'step'}</span>
              </div>
              <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                <Package className="h-4 w-4" />
                <span>{ingredienti.length} {ingredienti.length === 1 ? 'ingrediente' : 'ingredienti'}</span>
              </div>
            </div>

            {/* All Ingredients Card */}
            {ingredienti.length > 0 && (
              <div className="bg-white rounded-2xl border border-border p-4 space-y-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Ingredienti totali</p>
                <div className="divide-y divide-border/50">
                  {ingredienti.map((ing, i) => {
                    const unit = ing.is_acqua ? "ml" : udmInputLabel(ingredientiMap[ing.nome]?.unita_misura || ing.unita_misura || "g");
                    return (
                      <div key={i} className="flex items-center justify-between py-2.5">
                        <span className="text-sm font-medium text-[#1A1A1E]">{ing.nome}</span>
                        <span className="text-sm text-muted-foreground font-mono">{ing.quantita > 0 ? `${ing.quantita}${unit}` : '–'}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Fixed CTA */}
          <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#F5F5FA]/80 backdrop-blur-xl border-t border-border">
            <Button
              className="w-full h-14 text-base font-bold rounded-xl bg-[#6C63FF] hover:bg-[#5a52e0] text-white shadow-lg"
              onClick={() => { setShowOverview(false); setCurrentStep(0); }}
              data-testid="start-cooking-btn"
            >
              Inizia a Preparare
            </Button>
          </div>
        </div>
      );
    }

    // --- SCHERMATA STEP ---
    const stepImages = step.foto_urls || [];
    const stepIngredienti = step.ingredienti_usati || [];
    const countStepsWithIngredient = (nome) =>
      steps.filter(s => (s.ingredienti_usati || []).some(u => getIngNome(u) === nome)).length;

    return (
      <div className="min-h-screen bg-[#F5F5FA]" data-testid="procedura-readonly-step">
        {/* Header */}
        <div className="sticky top-0 z-10 backdrop-blur-xl bg-white/80 border-b border-border px-4 pt-3 pb-2 shadow-sm">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => setShowOverview(true)} data-testid="back-to-overview-btn">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <p className="flex-1 text-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Step {currentStep + 1} di {steps.length}
            </p>
            {canEdit ? (
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={() => navigate(`/anagrafica/ricette/${ricettaId}/procedura`)} className="h-9 w-9 text-blue-500" data-testid="edit-procedura-btn">
                  <ListChecks className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)} className="h-9 w-9 text-red-500" data-testid="delete-procedura-btn">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="w-9" />
            )}
          </div>
          {/* Stepper navigazione: Riepilogo + Step 1, 2, ... */}
          <div className="flex items-center mt-3 mb-1 overflow-x-auto gap-0" style={{ scrollbarWidth: 'none' }}>
            {/* Riepilogo */}
            <button
              onClick={() => setShowOverview(true)}
              className="flex-shrink-0 flex flex-col items-center gap-1 px-2"
              data-testid="nav-riepilogo"
            >
              <div className="w-9 h-9 rounded-full bg-muted border-2 border-border flex items-center justify-center">
                <ListChecks className="h-4 w-4 text-muted-foreground" />
              </div>
              <span className="text-[10px] font-medium text-muted-foreground">Ries.</span>
            </button>
            {steps.map((_, i) => (
              <div key={i} className="flex items-center flex-shrink-0">
                {/* Linea connettore */}
                <div className={`w-5 h-px flex-shrink-0 ${i <= currentStep ? 'bg-[#6C63FF]' : 'bg-border'}`} />
                {/* Cerchio step */}
                <button
                  onClick={() => setCurrentStep(i)}
                  className="flex-shrink-0 flex flex-col items-center gap-1 px-1"
                  data-testid={`nav-dot-${i}`}
                >
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-all ${
                    i === currentStep
                      ? 'bg-[#6C63FF] border-[#6C63FF] text-white shadow-md'
                      : i < currentStep
                        ? 'bg-[#6C63FF]/15 border-[#6C63FF]/40 text-[#6C63FF]'
                        : 'bg-muted border-border text-muted-foreground'
                  }`}>
                    {i + 1}
                  </div>
                  <span className={`text-[10px] font-medium ${i === currentStep ? 'text-[#6C63FF]' : 'text-muted-foreground'}`}>
                    Step {i + 1}
                  </span>
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="pb-28">
          {/* Images — full-width, una sotto l'altra (più chiare e leggibili in cucina) */}
          {stepImages.length > 0 && (
            <div className="flex flex-col gap-3 px-4 py-4">
              {stepImages.map((url, fIdx) => {
                const src = url.startsWith("http") ? url : fileUrl(url);
                return (
                  <img
                    key={fIdx}
                    src={src}
                    alt={`Step ${currentStep + 1} foto ${fIdx + 1}`}
                    className="w-full aspect-[4/3] object-cover rounded-2xl border border-border"
                  />
                );
              })}
            </div>
          )}

          <div className="px-4 space-y-4 pt-2">
            {/* Ingredients */}
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Ingredienti</p>
              {stepIngredienti.length === 0 ? (
                <p className="text-sm text-muted-foreground/60 italic py-1">Nessun ingrediente per questo step</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {stepIngredienti.map((u, i) => {
                    const nome = getIngNome(u);
                    const qty = getIngQty(u);
                    const ing = ingredientiMap[nome];
                    const unit = ing?.is_acqua ? "ml" : udmInputLabel(ing?.unita_misura || "g");
                    const stepsCount = countStepsWithIngredient(nome);
                    const displayQty = (stepsCount === 1 && ing?.quantita > 0) ? ing.quantita : qty;
                    return (
                      <div key={i} className="flex items-center gap-2 bg-white border border-border px-3 py-2 rounded-xl shadow-sm">
                        <span className="text-sm font-medium text-[#1A1A1E]">{nome}</span>
                        {displayQty > 0 && <span className="text-xs font-bold text-[#6C63FF]">{displayQty}{unit}</span>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Description */}
            {step.descrizione && (
              <div className="bg-white border border-border rounded-2xl p-5">
                <p className="text-base leading-relaxed text-[#1A1A1E]">{step.descrizione}</p>
              </div>
            )}
          </div>
        </div>

        {/* Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#F5F5FA]/80 backdrop-blur-xl border-t border-border flex gap-3">
          {currentStep > 0 && (
            <Button
              variant="outline"
              className="h-12 px-5 rounded-xl font-semibold"
              onClick={() => setCurrentStep(prev => prev - 1)}
              data-testid="prev-step-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" /> Indietro
            </Button>
          )}
          {currentStep < steps.length - 1 ? (
            <Button
              className="flex-1 h-12 rounded-xl font-bold bg-[#6C63FF] hover:bg-[#5a52e0] text-white"
              onClick={() => setCurrentStep(prev => prev + 1)}
              data-testid="next-step-btn"
            >
              Avanti <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="flex-1 h-12 rounded-xl font-bold bg-green-600 hover:bg-green-700 text-white"
              onClick={() => setShowOverview(true)}
              data-testid="finish-recipe-btn"
            >
              <Check className="h-4 w-4 mr-1" /> Ricetta Completata
            </Button>
          )}
        </div>

        {/* Confirm Delete Modal */}
        {confirmDelete && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setConfirmDelete(false)}>
            <div className="bg-white rounded-2xl p-6 max-w-sm w-full space-y-4 border border-border shadow-xl" onClick={e => e.stopPropagation()}>
              <h3 className="font-bold text-lg">Cancella Procedura</h3>
              <p className="text-sm text-muted-foreground">Sei sicuro? L&apos;azione è irreversibile.</p>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setConfirmDelete(false)}>Annulla</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteProcedura} data-testid="confirm-delete-procedura">Cancella</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // EDIT VIEW
  return (
    <div className="min-h-screen bg-background" data-testid="procedura-wizard">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-card border-b border-border px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn"><ChevronLeft className="h-5 w-5" /></Button>
          <div className="flex-1 min-w-0"><h1 className="font-bold text-base truncate">{ricettaNome}</h1><p className="text-xs text-muted-foreground">Step {currentStep + 1} di {steps.length}</p></div>
        </div>
        <div className="flex gap-1.5 mt-2">
          {steps.map((_, i) => (<button key={i} onClick={() => setCurrentStep(i)}
            className={`flex-1 py-1.5 rounded-md text-xs font-semibold transition-all ${i === currentStep ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
            data-testid={`step-tab-${i}`}>Step {i + 1}</button>))}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-4 pb-28">
        {/* Descrizione + Voice */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Descrizione</label>
            <Button type="button" variant={isListening ? "destructive" : "outline"} size="sm"
              onClick={isListening ? stopListening : startListening}
              className={`h-9 gap-1.5 ${isListening ? 'animate-pulse' : ''}`} data-testid="voice-btn">
              {isListening ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
              {isListening ? "Stop" : "Voce"}
            </Button>
          </div>
          <Textarea value={step.descrizione} onChange={(e) => { updateDescrizione(e.target.value); detectIngredientsInText(e.target.value); }}
            placeholder="Descrivi questo passaggio..."
            className="min-h-[100px] resize-none text-base" data-testid="step-descrizione" />
          {isListening && <div className="flex items-center gap-2 text-sm text-red-500"><div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />Ascolto...</div>}
        </div>

        {/* Foto */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Foto</label>
          <div className="flex flex-wrap gap-2">
            {(step.foto_urls || []).map((url, fIdx) => (
              <div key={fIdx} className="relative">
                <img src={url.startsWith("http") ? url : fileUrl(url)} alt={`Step ${currentStep + 1}`} className="w-20 h-20 object-cover rounded-lg border border-border" />
                <button type="button" onClick={() => removeFoto(fIdx)} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-destructive rounded-full flex items-center justify-center" data-testid={`remove-foto-${fIdx}`}><X className="h-3 w-3 text-white" /></button>
              </div>
            ))}
            <input type="file" accept="image/*" className="hidden" ref={fotoInputRef} onChange={(e) => handleFotoUpload(e.target.files)} />
            <button type="button" onClick={() => fotoInputRef.current?.click()} disabled={uploadingFoto}
              className="w-20 h-20 rounded-lg border-2 border-dashed border-border flex flex-col items-center justify-center text-muted-foreground hover:border-primary/50 transition-colors"
              data-testid="add-foto-btn">
              <ImagePlus className="h-5 w-5" /><span className="text-[10px] mt-1">{uploadingFoto ? "..." : "Foto"}</span>
            </button>
          </div>
        </div>

        {/* Ingredienti - Pillole read-only + pulsante per aprire drawer */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Ingredienti ({usatiInStep.length})</label>
            <Button type="button" variant="outline" size="sm" onClick={() => setIngDrawerOpen(true)}
              className="h-9 gap-1.5" data-testid="open-ing-drawer-btn">
              <Package className="h-3.5 w-3.5" />Gestisci
            </Button>
          </div>
          {usatiInStep.length === 0 ? (
            <button type="button" onClick={() => setIngDrawerOpen(true)}
              className="w-full py-4 rounded-xl border-2 border-dashed border-border text-center text-sm text-muted-foreground hover:border-primary/30 transition-colors"
              data-testid="empty-ing-btn">
              Tocca per aggiungere ingredienti a questo step
            </button>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {usatiInStep.map((u, i) => {
                const nome = getIngNome(u);
                const qty = getIngQty(u);
                const ing = ingredientiMap[nome];
                const unit = ing?.is_acqua ? "ml" : udmInputLabel(ing?.unita_misura || "g");
                return (
                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-green-600/10 text-green-500 border border-green-600/20 text-xs font-medium"
                    data-testid={`ing-pill-${i}`}>
                    {nome}{qty > 0 && <span className="font-bold">{qty}{unit}</span>}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Azioni step */}
        {steps.length > 1 && (
          <Button type="button" variant="outline" size="sm" onClick={removeCurrentStep}
            className="text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="remove-step-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina step
          </Button>
        )}
      </div>

      {/* Footer */}
      <div className="fixed bottom-0 left-0 right-0 bg-background border-t border-border p-4 flex gap-3">
        {currentStep > 0 && (
          <Button variant="outline" className="flex-shrink-0 h-12" onClick={() => setCurrentStep(prev => prev - 1)} data-testid="prev-step-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        )}
        {currentStep < steps.length - 1 ? (
          <Button className="flex-1 h-12" onClick={() => setCurrentStep(prev => prev + 1)} data-testid="next-step-btn">
            Avanti <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <>
            <Button className="flex-1 h-12 font-bold" onClick={addStep} data-testid="add-step-btn">
              <Plus className="h-4 w-4 mr-1" /> Nuovo Step
            </Button>
            <Button className="flex-1 h-12 bg-green-600 hover:bg-green-700 text-white font-bold" onClick={() => setShowSummary(true)} data-testid="show-summary-btn">
              <Check className="h-4 w-4 mr-1" /> Riepilogo
            </Button>
          </>
        )}
      </div>

      {/* Drawer ingredienti */}
      <IngredientiStepDrawer
        open={ingDrawerOpen}
        onOpenChange={setIngDrawerOpen}
        ingredienti={ingredienti}
        stepData={step}
        currentStep={currentStep}
        steps={steps}
        onToggle={toggleIngrediente}
        onUpdateQty={updateIngredienteQuantita}
        onSetTutto={setTuttoIngrediente}
        getRemainingQty={getRemainingQty}
      />
    </div>
  );
};

export default ProceduraRicetta;
